import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Laboratory, LaboratoryWithId } from '../types';
import { laboratoriesCollection } from '../firebase/schema';
import { addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { validateLaboratoryData } from '../utils/validation';
import { useAuthStore } from './authStore';

export const useLaboratoryStore = defineStore('laboratory', () => {
  const laboratories = ref<LaboratoryWithId[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const authStore = useAuthStore();

  const fetchLaboratories = async () => {
    // Check if user is authenticated
    if (!authStore.user) {
      console.error('Attempted to fetch laboratories without authentication');
      return;
    }

    loading.value = true;
    try {
      const snapshot = await getDocs(laboratoriesCollection);
      laboratories.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LaboratoryWithId[];
    } catch (err) {
      console.error('Error fetching laboratories:', err);
      error.value = 'Error al cargar laboratorios';
    } finally {
      loading.value = false;
    }
  };

  const addLaboratory = async (laboratoryData: Laboratory) => {
    if (!validateLaboratoryData(laboratoryData)) {
      throw new Error('Datos del laboratorio inválidos');
    }

    try {
      const docRef = await addDoc(laboratoriesCollection, laboratoryData);
      await fetchLaboratories();
      return docRef.id;
    } catch (err) {
      console.error('Error adding laboratory:', err);
      throw new Error('Error al agregar laboratorio');
    }
  };

  const updateLaboratory = async (id: string, data: Partial<Laboratory>) => {
    try {
      await updateDoc(doc(laboratoriesCollection, id), data);
      await fetchLaboratories();
    } catch (err) {
      console.error('Error updating laboratory:', err);
      throw new Error('Error al actualizar laboratorio');
    }
  };

  const deleteLaboratory = async (id: string) => {
    try {
      await deleteDoc(doc(laboratoriesCollection, id));
      await fetchLaboratories();
    } catch (err) {
      console.error('Error deleting laboratory:', err);
      throw new Error('Error al eliminar laboratorio');
    }
  };

  // Remove automatic initialization
  // Only fetch when explicitly called after authentication

  return {
    laboratories,
    loading,
    error,
    fetchLaboratories,
    addLaboratory,
    updateLaboratory,
    deleteLaboratory
  };
});